/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import React, {useContext, useRef, useState} from 'react';
import {
    ActionGroup,
    Button,
    ButtonType,
    ButtonVariant,
    DataListAction,
    DataListCell,
    DataListItem,
    DataListItemCells,
    DataListItemRow,
    Dropdown,
    DropdownItem,
    DropdownList,
    Form,
    FormContextProvider,
    MenuToggle,
    MenuToggleElement,
    Modal,
    ModalVariant,
    TextInput
} from '@patternfly/react-core';
import {executeAndSend} from "../../fk/core/websocket_event_source";
import {DeleteBacklog, RenameBacklog} from "../../fk/core/backlog";
import DataContext from '../context/DataContext';
import EllipsisVIcon from '@patternfly/react-icons/dist/esm/icons/ellipsis-v-icon';
import TrashIcon from "@patternfly/react-icons/dist/esm/icons/trash-icon";
import EditIcon from "@patternfly/react-icons/dist/esm/icons/edit-icon";

type BacklogDataListItemProps = {
    id: string,
    name: string,
    nbItems: number,
    setSelectedBacklogId: Function
}

export function BacklogDataListItem(props: BacklogDataListItemProps) {

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const ref = useRef<HTMLInputElement>(null);

    const { data, setData } = useContext(DataContext);

    const onDropdownToggle = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const onDropdownSelect = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const renameBacklog = (id: string, newName: string) => {
        if (data) {
            executeAndSend(setData, RenameBacklog, id, newName);
        } else {
            throw new Error("Trying to rename a backlog when there's no data");
        }
    }

    const deleteBacklog = (id: string) => {
        if (data) {
            executeAndSend(setData, DeleteBacklog, id);
        } else {
            throw new Error("Trying to delete a backlog when there's no data");
        }
    }

    const deleteBacklogModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"Confirmation"}
                titleIconVariant="danger"
                isOpen={isDeleteModalOpen}
                onClose={handleCancelDelete}
                actions={[
                    <Button key="confirm" variant="danger" onClick={onAcceptDelete}>
                        Delete
                    </Button>,
                    <Button key="cancel" variant="link" onClick={handleCancelDelete}>
                        Cancel
                    </Button>
                ]}>
                Are you sure you want to delete "{props.name}"?
            </Modal>
        );
    }

    const handleCancelDelete = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const onAcceptDelete = () => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
        deleteBacklog(props.id);
        props.setSelectedBacklogId("");
    }

    const editBacklogModal = () => {
        return (
            <Modal
                variant={ModalVariant.medium}
                title={"Edit Backlog"}
                isOpen={isEditModalOpen}
                onClose={handleCancelEdit}
            >
                <FormContextProvider initialValues={{'backlog-name': props.name}}>
                    {({setValue, getValue, setError}) => (
                        <Form>
                            <TextInput
                                id="backlog-name"
                                ref={ref}
                                onFocus={() => ref?.current?.select()}
                                onChange={(_event, value) => {
                                    setValue('backlog-name', value);
                                    setError('backlog-name', undefined);
                                }}
                                value={getValue('backlog-name')}/>
                            <ActionGroup>
                                <Button
                                    type={ButtonType.submit}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onAcceptEdit(getValue('backlog-name'));
                                    }}>Save</Button>
                                <Button
                                    type={ButtonType.button}
                                    variant={ButtonVariant.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCancelEdit(e);
                                    }}>Cancel</Button>
                            </ActionGroup>
                        </Form>)}
                </FormContextProvider>
            </Modal>
        );
    }

    const handleCancelEdit = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsEditModalOpen(!isEditModalOpen);
    };

    const onAcceptEdit = (newName: string) => {
        renameBacklog(props.id, newName);
        setIsEditModalOpen(!isEditModalOpen);
    }

    return (
        <>
            {deleteBacklogModal()}
            {editBacklogModal()}
            <DataListItem aria-labelledby={props.id + '-item'} id={props.id}>
                <DataListItemRow>
                    <DataListItemCells
                        dataListCells={[
                            <DataListCell key={props.id + '-item'}>
                                {props.name}
                            </DataListCell>
                        ]}
                    />
                    <DataListAction
                        aria-labelledby="multi-actions-item1 multi-actions-action1"
                        id={props.id + "item-actions"}
                        aria-label="Actions"
                        isPlainButtonAction>
                        <Dropdown
                            popperProps={{ position: 'right' }}
                            onSelect={onDropdownSelect}
                            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                                <MenuToggle
                                    ref={toggleRef}
                                    isExpanded={isDropdownOpen}
                                    onClick={onDropdownToggle}
                                    variant="plain"
                                    aria-label="Data list with actions example kebab toggle">
                                    <EllipsisVIcon aria-hidden="false" />
                                </MenuToggle>
                            )}
                            isOpen={isDropdownOpen}
                            onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}>
                            <DropdownList>
                                <DropdownItem
                                    key="edit"
                                    icon={<EditIcon />}
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        setIsEditModalOpen(true);
                                    }}>Edit</DropdownItem>
                                <DropdownItem
                                    key="delete"
                                    icon={<TrashIcon />}
                                    isDanger
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        setIsDeleteModalOpen(true);
                                    }}>Delete</DropdownItem>
                            </DropdownList>
                        </Dropdown>
                    </DataListAction>
                </DataListItemRow>
            </DataListItem>
        </>
    );
}
